import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { getHumanToken, setHumanCheckSignedIn } from "@/lib/humanCheck";
import { useGoogleLogin } from "@react-oauth/google";
import { getReferralCode } from "@/lib/referral";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || "";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Subscription tier is fetched separately from /auth/me -- has_download_access
  // (one-time Day Pass/Monthly purchases) and subscription tier are two
  // independent flags, and several premium-only features (Talent Directory,
  // saved job searches) must check the latter specifically.
  const [subscription, setSubscription] = useState(null);
  // Signed-in users skip the human check (lib/humanCheck.js).
  useEffect(() => { setHumanCheckSignedIn(loading ? null : !!user); }, [user, loading]);

  const refreshSubscription = useCallback(async () => {
    try {
      const r = await fetch(`${API}/payments/stripe/subscription`, { credentials: "include" });
      setSubscription(r.ok ? await r.json() : null);
    } catch {
      setSubscription(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (!r.ok) throw new Error("not authed");
      setUser(await r.json());
      await refreshSubscription();
    } catch {
      setUser(null);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [refreshSubscription]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Own Google OAuth — authorization-code popup flow. Exchanges the code on our backend.
  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (resp) => {
      try {
        const r = await fetch(`${API}/auth/google`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ code: resp.code, referral_code: getReferralCode() }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.detail || "Google sign-in failed");
        // Matched account has MFA enabled -- Google verifying the email
        // isn't the second factor, so no session was issued yet (see
        // auth-service's _complete_login). Hand off to the same MFA
        // challenge UI the password-login flow already has.
        if (data.mfa_required) {
          window.dispatchEvent(new CustomEvent("auth:mfa-required", { detail: { pendingId: data.pending_id } }));
          return;
        }
        setUser(data);
        refreshSubscription();
        window.dispatchEvent(new CustomEvent("auth:success"));
      } catch (e) {
        window.dispatchEvent(new CustomEvent("auth:error", { detail: e.message }));
      }
    },
    onError: () => window.dispatchEvent(new CustomEvent("auth:error", { detail: "Google sign-in was cancelled." })),
  });

  const login = (returnToEditor = true) => {
    if (returnToEditor) localStorage.setItem("resume_studio_post_login", "editor");
    googleLogin();
  };

  // GitHub has no popup SDK like @react-oauth/google -- its OAuth Apps only
  // support a full-page redirect to a single, pre-registered callback URL,
  // so this navigates away entirely rather than opening a popup. The
  // callback route (GitHubAuthCallback below) picks the flow back up.
  const loginWithGithub = (returnToEditor = true) => {
    if (returnToEditor) localStorage.setItem("resume_studio_post_login", "editor");
    if (!GITHUB_CLIENT_ID) {
      window.dispatchEvent(new CustomEvent("auth:error", { detail: "GitHub sign-in is not configured." }));
      return;
    }
    const redirectUri = `${window.location.origin}/auth/github/callback`;
    const params = new URLSearchParams({
      client_id: GITHUB_CLIENT_ID,
      redirect_uri: redirectUri,
      scope: "read:user user:email",
    });
    window.location.href = `https://github.com/login/oauth/authorize?${params.toString()}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    setUser(null);
    setSubscription(null);
  };

  const formatErr = (detail, fallback) => {
    if (detail == null) return fallback || "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || fallback;
    if (detail && typeof detail.msg === "string") return detail.msg;
    return fallback || "Something went wrong. Please try again.";
  };

  const loginWithPassword = async (email, password) => {
    const r = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatErr(data.detail, "Could not sign you in."));
    if (data.mfa_required) return data; // { mfa_required: true, pending_id } -- caller must prompt for a code
    setUser(data);
    refreshSubscription();
    return data;
  };

  const completeMfaLogin = async (pendingId, code) => {
    const r = await fetch(`${API}/auth/mfa/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ pending_id: pendingId, code }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatErr(data.detail, "Invalid authentication code."));
    setUser(data);
    refreshSubscription();
    return data;
  };

  const register = async (email, password, name) => {
    // Real-human check before creating an account (2026-10-01).
    let turnstileToken = null;
    try { turnstileToken = await getHumanToken(); } catch { throw new Error("Please complete the human check to create your account."); }
    const r = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password, name, origin_url: window.location.origin, referral_code: getReferralCode(), turnstile_token: turnstileToken }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatErr(data.detail, "Could not create your account."));
    // Password registration no longer logs in immediately -- verify_email_required
    // means no session was issued (see auth-service's /auth/register), so there's
    // nothing to set here; the caller shows a "check your email" state instead.
    if (data.verification_required) return data;
    setUser(data);
    refreshSubscription();
    return data;
  };

  const verifyEmail = async (token) => {
    const r = await fetch(`${API}/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatErr(data.detail, "Could not verify your email."));
    setUser(data);
    refreshSubscription();
    return data;
  };

  const resendVerification = async (email) => {
    const r = await fetch(`${API}/auth/resend-verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, origin_url: window.location.origin }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatErr(data.detail, "Could not resend the verification email."));
    return data;
  };

  const forgotPassword = async (email) => {
    const r = await fetch(`${API}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, origin_url: window.location.origin }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatErr(data.detail, "Could not send reset email."));
    return data;
  };

  const resetPassword = async (token, password) => {
    const r = await fetch(`${API}/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(formatErr(data.detail, "Could not reset your password."));
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user, loading, checkAuth, login, loginWithGithub, loginWithPassword, completeMfaLogin, register, verifyEmail, resendVerification, forgotPassword, resetPassword, logout,
        hasAccess: !!user?.has_download_access,
        subscription,
        tier: subscription?.tier || null,
        isPremium: subscription?.tier === "premium",
        // Effective plan for feature locks (lib/plans.js) -- mirrors the
        // backend's common/plans.resolve_plan: an active subscription tier,
        // else a legacy paid pass counts as Starter, else Free.
        plan: ["active", "trialing", "past_due"].includes(subscription?.status) && subscription?.tier
          ? subscription.tier
          : user?.has_download_access ? "starter" : "free",
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Handles the #session_id= fragment returned by Emergent Google Auth
export const AuthCallback = () => {
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    (async () => {
      if (sid) {
        try {
          const r = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "X-Session-ID": sid },
            credentials: "include",
          });
          const data = await r.json().catch(() => ({}));
          if (data.mfa_required) {
            // Matched account has MFA enabled -- no session was issued yet
            // (see auth-service's _complete_login). Same hand-off pattern as
            // GitHubAuthCallback: a full-page redirect flow, so /login reads
            // this via a query param on mount rather than the modal's event.
            window.location.href = `/login?mfa_pending=${encodeURIComponent(data.pending_id)}`;
            return;
          }
        } catch (e) {
          console.error("Auth session exchange failed", e);
        }
      }
      // Canva-style post-login routing (2026-09-01): land signed-in users
      // in the real workspace, not the marketing page, on a normal
      // successful exchange.
      window.location.href = "/dashboard";
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0F211B] flex flex-col items-center justify-center text-white gap-3">
      <div className="w-6 h-6 border-2 border-[#A67C52] border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-neutral-400 font-mono">Signing you in…</p>
    </div>
  );
};

// Handles the ?code= query param GitHub redirects back to /auth/github/callback
// with (a real route, unlike Google's popup flow -- see loginWithGithub above).
export const GitHubAuthCallback = () => {
  const processed = useRef(false);
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    (async () => {
      if (code) {
        try {
          const r = await fetch(`${API}/auth/github`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ code, referral_code: getReferralCode() }),
          });
          const data = await r.json().catch(() => ({}));
          if (!r.ok) {
            console.error("GitHub auth exchange failed", data.detail);
          } else if (data.mfa_required) {
            // Matched account has MFA enabled -- no session was issued yet
            // (see auth-service's _complete_login). This is a full-page
            // redirect flow, not the modal's event-listener setup, so hand
            // off via a query param /login itself reads on mount instead.
            window.location.href = `/login?mfa_pending=${encodeURIComponent(data.pending_id)}`;
            return;
          }
        } catch (e) {
          console.error("GitHub auth exchange failed", e);
        }
      }
      // Full reload (not client-side navigate) so AuthProvider's checkAuth()
      // re-runs fresh and picks up the session cookie just set above.
      // Canva-style post-login routing (2026-09-01): land signed-in users
      // in the real workspace, not the marketing page.
      window.location.href = "/dashboard";
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0F211B] flex flex-col items-center justify-center text-white gap-3">
      <div className="w-6 h-6 border-2 border-[#A67C52] border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-neutral-400 font-mono">Signing you in…</p>
    </div>
  );
};
